import React, { useState, useEffect } from 'react';

const EventEditModal = ({ event, isOpen, onClose, onSave, availableImages, onUpload }) => {
    const [formData, setFormData] = useState({
        title: '',
        date: '',
        displayDate: '',
        location: '',
        address: '',
        printTicketUrl: '',
        onlineTicketUrl: '',
        photosUrl: '',
        images: [],
        showButtons: true
    });
    const [isUploading, setIsUploading] = useState(false);

    useEffect(() => {
        if (event) {
            setFormData({
                title: event.title || '',
                date: event.date || '',
                displayDate: event.displayDate || '',
                location: event.location || '',
                address: event.address || '',
                printTicketUrl: event.printTicketUrl || '',
                onlineTicketUrl: event.onlineTicketUrl || '',
                photosUrl: event.photosUrl || '',
                images: event.images || [],
                showButtons: event.showButtons !== undefined ? event.showButtons : true
            });
        } else {
            setFormData({
                title: '',
                date: '',
                displayDate: '',
                location: '',
                address: '',
                printTicketUrl: '',
                onlineTicketUrl: '',
                photosUrl: '',
                images: [],
                showButtons: true
            });
        }
    }, [event]);

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave({ ...event, ...formData });
    };

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        setIsUploading(true);
        try {
            await onUpload(files);
            // Images will be added via the select dropdown after upload
        } catch (error) {
            console.error("Upload failed in modal:", error);
        } finally {
            setIsUploading(false);
        }
    };

    const handleAddImage = (imagePath) => {
        if (imagePath && !formData.images.includes(imagePath)) {
            setFormData(prev => ({
                ...prev,
                images: [...prev.images, imagePath]
            }));
        }
    };

    const handleRemoveImage = (index) => {
        setFormData(prev => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index)
        }));
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl my-8">
                <h2 className="text-2xl font-bold mb-4">{event ? 'Edit Event' : 'Add New Event'}</h2>
                <form onSubmit={handleSubmit} className="max-h-[70vh] overflow-y-auto">
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Event Title</label>
                        <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            required
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Date (YYYY-MM-DD)</label>
                            <input
                                type="date"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                value={formData.date}
                                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Display Date</label>
                            <input
                                type="text"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                value={formData.displayDate}
                                onChange={(e) => setFormData({ ...formData, displayDate: e.target.value })}
                                placeholder="February 7th, 2026"
                                required
                            />
                        </div>
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                        <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                            value={formData.location}
                            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                            required
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                        <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            placeholder="4601 Bosque Blvd, Waco, TX"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Print Ticket URL</label>
                            <input
                                type="url"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                value={formData.printTicketUrl}
                                onChange={(e) => setFormData({ ...formData, printTicketUrl: e.target.value })}
                                placeholder="https://..."
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Online Ticket URL</label>
                            <input
                                type="text"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                value={formData.onlineTicketUrl}
                                onChange={(e) => setFormData({ ...formData, onlineTicketUrl: e.target.value })}
                                placeholder="/online-ticket"
                            />
                        </div>
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Photos URL</label>
                        <input
                            type="url"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                            value={formData.photosUrl}
                            onChange={(e) => setFormData({ ...formData, photosUrl: e.target.value })}
                            placeholder="https://photos.google.com/..."
                        />
                    </div>
                    <div className="mb-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
                                checked={formData.showButtons}
                                onChange={(e) => setFormData({ ...formData, showButtons: e.target.checked })}
                            />
                            <span className="text-sm font-medium text-gray-700">Show ticket buttons (for upcoming events)</span>
                        </label>
                    </div>
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Event Images/Tickets</label>
                        <div className="flex gap-2 mb-2">
                            <select
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                                onChange={(e) => {
                                    if (e.target.value) {
                                        handleAddImage(e.target.value);
                                        e.target.value = '';
                                    }
                                }}
                            >
                                <option value="">Add an image...</option>
                                {availableImages.map((img) => (
                                    <option key={img} value={img}>
                                        {img.split('/').pop()}
                                    </option>
                                ))}
                            </select>
                            <label className={`px-4 py-2 bg-gray-200 text-gray-700 rounded-md cursor-pointer hover:bg-gray-300 transition-colors ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                <input
                                    type="file"
                                    accept="image/*,.pdf"
                                    multiple
                                    onChange={handleFileChange}
                                    disabled={isUploading}
                                    className="hidden"
                                />
                                {isUploading ? '...' : 'Upload'}
                            </label>
                        </div>

                        {formData.images.length > 0 && (
                            <div className="mt-2 space-y-2">
                                <p className="text-xs text-gray-500">Selected images:</p>
                                {formData.images.map((img, index) => (
                                    <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                                        <span className="text-sm flex-1">{img.split('/').pop()}</span>
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveImage(index)}
                                            className="text-red-600 hover:text-red-800 text-sm"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
                        >
                            Save
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EventEditModal;
